import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import CommaSeparatedListParamConfigForm from '../components/Forms/CommaSeparatedListParamConfigForm';
import DemoWhitelistParamConfigForm from '../components/Forms/DemoWhitelistParamConfigForm';
import SalesAppVersionParamConfigForm from '../components/Forms/SalesAppVersionParamConfigForm';
import TextParamConfigForm from '../components/Forms/TextParamConfigForm';
import { ModalTypes } from '../components/Modals/ModalTypes';
import * as ModalsActions from '../redux/modals/modals.actions';
import * as SettingsActions from '../redux/settings/settings.actions';
import { IState } from '../redux/store';
import {
  DESKTOP_APP_LAST_VERSION_PARAM_KEY,
  LUCKEY_DEMO_WEEKLY_CLEANUP_WHITELIST_PARAM_KEY,
  SALES_APP_VERSION_PARAM_KEY,
  WALLET_DISABLE_EXCLUSION_IDS_PARAM_KEY,
} from '../utils/AppConstants';
import { getParamConfigValue } from '../utils/ParamConfigUtils';

const styles = createStyles({
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 20,
    paddingRight: 20,
  },
  content: {
    padding: 20,
  },
});

type IReduxProps = ConnectedProps<typeof connector>;

export interface ISettingsPageProps extends WithStyles<typeof styles> {}

export type ComponentProps = ISettingsPageProps & IReduxProps;

export interface ISettingsPageState {
  isSaving: boolean;
}

class SettingsPage extends React.Component<ComponentProps, ISettingsPageState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      isSaving: false,
    };
    this.onSaveParamConfig = this.onSaveParamConfig.bind(this);
    this.onSaveSalesAppVersion = this.onSaveSalesAppVersion.bind(this);
  }

  async componentDidMount() {
    const { dispatch } = this.props;
    await dispatch<any>(SettingsActions.fetchParamConfigurations({ page: 0, pageSize: 500 }));
  }

  hasExistingConfig(key: string): boolean {
    const { paramConfigs } = this.props;
    return !!_.find(paramConfigs, { keyConfig: key });
  }

  async onSaveParamConfig(key: string, value: string) {
    const { dispatch } = this.props;
    this.setState({ isSaving: true });
    try {
      await dispatch<any>(SettingsActions.saveParamConfigurationByKey(key, value));
      dispatch<any>(ModalsActions.showModal('SUCCESS_MODAL', {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'settings.saveSuccess',
        },
      }));
    } catch (error) {
      dispatch<any>(ModalsActions.showModal('ERROR_MODAL', {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'settings.saveError',
        },
      }));
    } finally {
      this.setState({ isSaving: false });
    }
  }

  async onSaveSalesAppVersion(value: string) {
    await this.onSaveParamConfig(SALES_APP_VERSION_PARAM_KEY, value);
  }

  render() {
    const { classes, paramConfigs } = this.props;
    const { isSaving } = this.state;

    return (
      <div style={{ maxHeight: 'calc(100vh - 64px)', minHeight: 'calc(100vh - 64px)' }}>
        <div className={classes.header}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
            {'Vasho Settings'}
          </h2>
        </div>
        <div className={classes.content}>
          <SalesAppVersionParamConfigForm
            initialValue={getParamConfigValue(paramConfigs, SALES_APP_VERSION_PARAM_KEY)}
            hasExistingConfig={this.hasExistingConfig(SALES_APP_VERSION_PARAM_KEY)}
            onSave={this.onSaveSalesAppVersion}
            isSaving={isSaving}
          />
          <TextParamConfigForm
            titleLanguageKey="settings.desktopAppLastVersion"
            descriptionLanguageKey="settings.desktopAppLastVersionDescription"
            initialValue={getParamConfigValue(paramConfigs, DESKTOP_APP_LAST_VERSION_PARAM_KEY)}
            hasExistingConfig={this.hasExistingConfig(DESKTOP_APP_LAST_VERSION_PARAM_KEY)}
            onSave={(value) => this.onSaveParamConfig(DESKTOP_APP_LAST_VERSION_PARAM_KEY, value)}
            isSaving={isSaving}
          />
          <DemoWhitelistParamConfigForm
            initialValue={getParamConfigValue(paramConfigs, LUCKEY_DEMO_WEEKLY_CLEANUP_WHITELIST_PARAM_KEY)}
            hasExistingConfig={this.hasExistingConfig(LUCKEY_DEMO_WEEKLY_CLEANUP_WHITELIST_PARAM_KEY)}
            onSave={(value) => this.onSaveParamConfig(LUCKEY_DEMO_WEEKLY_CLEANUP_WHITELIST_PARAM_KEY, value)}
            isSaving={isSaving}
          />
          <CommaSeparatedListParamConfigForm
            titleLanguageKey="settings.walletDisableExclusionIds"
            descriptionLanguageKey="settings.walletDisableExclusionIdsDescription"
            initialValue={getParamConfigValue(paramConfigs, WALLET_DISABLE_EXCLUSION_IDS_PARAM_KEY)}
            hasExistingConfig={this.hasExistingConfig(WALLET_DISABLE_EXCLUSION_IDS_PARAM_KEY)}
            onSave={(value) => this.onSaveParamConfig(WALLET_DISABLE_EXCLUSION_IDS_PARAM_KEY, value)}
            isSaving={isSaving}
            placeholder="12345"
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    paramConfigs: state.settings.paramConfigs.data.content,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(SettingsPage as any));
